/**
 * URL-change event — fills a gap in the Web platform.
 *
 * The browser fires `hashchange` and `popstate`, but NOT for `pushState` /
 * `replaceState`. Next.js's App Router uses `pushState` for client-side hash
 * navigation (e.g. `<a href="#c17">` → smooth scroll + URL update without
 * full route change), so listening to `hashchange` alone misses these.
 *
 * This module patches `history.pushState`/`replaceState` once and dispatches
 * a custom `app:urlchange` event covering all four cases:
 *   - pushState (Next.js Link, manual history.pushState)
 *   - replaceState
 *   - popstate (back/forward)
 *   - hashchange (classic `<a href="#…">` outside of Next Link)
 *
 * Idempotent: ensureUrlChangeEvent() can be called repeatedly. Originals are
 * still invoked, so analytics/tracking that already hooked into pushState
 * keep working.
 */

export const URL_CHANGE_EVENT = 'app:urlchange';

declare global {
  interface Window {
    __urlChangePatchInstalled?: boolean;
  }
}

export function ensureUrlChangeEvent(): void {
  if (typeof window === 'undefined') return;
  if (window.__urlChangePatchInstalled) return;
  window.__urlChangePatchInstalled = true;

  const dispatch = () => window.dispatchEvent(new Event(URL_CHANGE_EVENT));

  const origPushState = history.pushState.bind(history);
  history.pushState = function (
    ...args: Parameters<typeof history.pushState>
  ) {
    origPushState(...args);
    dispatch();
  };

  const origReplaceState = history.replaceState.bind(history);
  history.replaceState = function (
    ...args: Parameters<typeof history.replaceState>
  ) {
    origReplaceState(...args);
    dispatch();
  };

  window.addEventListener('popstate', dispatch);
  window.addEventListener('hashchange', dispatch);
}
